/**
 * IntegrationTestModal — in-app simulator for inbound email and webhook calls
 * to the Lieferanten AP, useful for demos and end-to-end verification without
 * a real mail server or external caller.
 *
 * Email tab:   compose a fake e-mail with optional file attachment → POST /email
 * Webhook tab: enter a text payload that gets base64-wrapped → POST /webhook
 *
 * On success, navigates to the landing page in the current tab so the user
 * sees the notification badge on the AP card immediately.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconMail,
  IconPaperclip,
  IconPlugConnected,
  IconSend,
  IconX,
} from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useRef, useState } from "react"
import { useNavigate } from "react-router"
import { api } from "../../../api/client"

interface Props {
  apId: string
  emailAddressDisplay?: string
  onClose: () => void
}

type Tab = "email" | "webhook"

export function IntegrationTestModal({
  apId,
  emailAddressDisplay,
  onClose,
}: Props) {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>("email")

  const { data: settings } = useQuery({
    queryKey: ["ap-settings", apId],
    queryFn: () => api.apSettings(apId),
  })

  const emailAddress = emailAddressDisplay ?? settings?.email_address ?? ""

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["pendingDocuments", apId] })
    setTimeout(() => navigate("/qms-database"), 1200)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div
        className="relative flex w-[640px] max-w-full flex-col rounded-xl border border-grey4 bg-grey1 shadow-2xl"
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-grey4 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-white">
              {translate("QMS/SUPPLIER/TEST_INTEGRATION")}
            </h2>
            <p className="mt-0.5 text-xs text-grey5">
              {translate("QMS/SUPPLIER/SIMULATE_TITLE")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-grey5 hover:bg-grey3 hover:text-white"
          >
            <IconX size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex shrink-0 gap-1 border-b border-grey4 px-5 py-2">
          <TabButton
            active={activeTab === "email"}
            onClick={() => setActiveTab("email")}
            icon={<IconMail size={14} />}
            label={translate("QMS/INTEGRATION/SIMULATE_EMAIL")}
          />
          <TabButton
            active={activeTab === "webhook"}
            onClick={() => setActiveTab("webhook")}
            icon={<IconPlugConnected size={14} />}
            label={translate("QMS/INTEGRATION/SIMULATE_WEBHOOK")}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "email" ? (
            <EmailForm
              apId={apId}
              emailAddress={emailAddress}
              onSuccess={onSuccess}
            />
          ) : (
            <WebhookForm
              apId={apId}
              webhookUrl={settings?.webhook_url ?? ""}
              webhookToken={settings?.webhook_token ?? ""}
              onSuccess={onSuccess}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Tab button ─────────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
        active ? "bg-grey3 text-white" : "text-grey5 hover:text-grey6",
      )}
    >
      {icon}
      {label}
    </button>
  )
}

// ── E-Mail form ────────────────────────────────────────────────────────────────

function EmailForm({
  apId,
  emailAddress,
  onSuccess,
}: {
  apId: string
  emailAddress: string
  onSuccess: () => void
}) {
  const { translate } = useTranslation()
  const [from, setFrom] = useState("lieferant@beispiel.de")
  const [subject, setSubject] = useState("SVHC-Deklaration 2025")
  const [body, setBody] = useState(translate("QMS/SUPPLIER/EMAIL_BODY_DEFAULT"))
  const [attachedFile, setAttachedFile] = useState<File | null>(null)
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<{
    kind: "ok" | "err"
    msg: string
  } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleSend = async () => {
    setSending(true)
    setStatus(null)
    try {
      let file: File
      if (attachedFile) {
        file = attachedFile
      } else {
        const content = `Von: ${from}\nAn: ${emailAddress}\nBetreff: ${subject}\n\n${body}`
        file = new File(
          [content],
          `${subject.replace(/[^a-zA-Z0-9\s-]/g, "").replace(/\s+/g, "-")}.txt`,
          { type: "text/plain" },
        )
      }
      await api.pendingDocumentEmailSimulate(apId, file)
      setStatus({ kind: "ok", msg: translate("QMS/SUPPLIER/EMAIL_RECEIVED") })
      onSuccess()
    } catch (err) {
      setStatus({
        kind: "err",
        msg: err instanceof Error ? err.message : translate("QMS/INTEGRATION/SEND_ERROR"),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-grey5">
        {translate("QMS/SUPPLIER/SIMULATE_EMAIL_DESC")}
        {translate("QMS/SUPPLIER/BODY_AS_TXT")}
      </p>

      <div className="space-y-3">
        <FormField label={translate("QMS/INTEGRATION/FIELD_FROM")}>
          <input
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="w-full rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-sm text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_TO")}>
          <input
            value={emailAddress || "…"}
            readOnly
            className="w-full cursor-default rounded-md border border-grey4 bg-grey3 px-3 py-1.5 text-sm text-grey5 outline-hidden"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_SUBJECT")}>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-sm text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_MESSAGE")}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full resize-none rounded-md border border-grey4 bg-grey2 px-3 py-2 text-sm leading-relaxed text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_ATTACHMENT")}>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-md border border-grey4 px-3 py-1.5 text-xs text-grey5 hover:border-grey5 hover:text-white"
            >
              <IconPaperclip size={13} />
              {attachedFile
                ? attachedFile.name
                : translate("QMS/SUPPLIER/CHOOSE_FILE")}
            </button>
            {attachedFile && (
              <button
                onClick={() => {
                  setAttachedFile(null)
                  if (fileRef.current) fileRef.current.value = ""
                }}
                className="text-xs text-grey5 hover:text-white"
              >
                {translate("QMS/SUPPLIER/REMOVE")}
              </button>
            )}
            {!attachedFile && (
              <span className="text-[11px] italic text-grey5">
                {translate("QMS/SUPPLIER/ATTACHMENT_OPTIONAL_HINT")}
              </span>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => setAttachedFile(e.target.files?.[0] ?? null)}
          />
        </FormField>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSend}
          disabled={sending}
          className="inline-flex items-center gap-2 rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-grey1 hover:opacity-90 disabled:opacity-40"
        >
          <IconSend size={14} />
          {sending
            ? translate("QMS/SUPPLIER/SENDING")
            : translate("QMS/SUPPLIER/SEND_EMAIL")}
        </button>
        {status && (
          <span
            className={clsx(
              "text-xs",
              status.kind === "ok" ? "text-success" : "text-red1",
            )}
          >
            {status.msg}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Webhook form ───────────────────────────────────────────────────────────────

function WebhookForm({
  apId,
  webhookUrl,
  webhookToken,
  onSuccess,
}: {
  apId: string
  webhookUrl: string
  webhookToken: string
  onSuccess: () => void
}) {
  const { translate } = useTranslation()
  const [filename, setFilename] = useState("lieferant-svhc-2025.txt")
  const [payload, setPayload] = useState(
    "Lieferanten-SVHC-Deklaration 2025\n\nCAS-Nr: 1234-56-7\nBezeichnung: Blei\nGehalt: 0.002 %\n",
  )
  const [sending, setSending] = useState(false)
  const [status, setStatus] = useState<{
    kind: "ok" | "err"
    msg: string
  } | null>(null)

  const handleSend = async () => {
    setSending(true)
    setStatus(null)
    try {
      await api.pendingDocumentWebhookSimulate(
        apId,
        webhookToken,
        filename,
        payload,
      )
      setStatus({ kind: "ok", msg: translate("QMS/SUPPLIER/WEBHOOK_RECEIVED") })
      onSuccess()
    } catch (err) {
      setStatus({
        kind: "err",
        msg: err instanceof Error ? err.message : translate("QMS/INTEGRATION/SEND_ERROR"),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-grey5">
        {translate("QMS/SUPPLIER/SIMULATE_WEBHOOK_DESC")}
        {translate("QMS/INTEGRATION/TRANSMITTED_AS_TXT")}
      </p>

      <div className="rounded-lg border border-grey4 bg-grey2 p-3">
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-grey5">
          {translate("QMS/INTEGRATION/ENDPOINT_LABEL")}
        </p>
        <code
          className="font-mono block truncate text-xs text-white"
          title={`POST ${webhookUrl}`}
        >
          POST {webhookUrl || "…"}
        </code>
      </div>

      <div className="space-y-3">
        <FormField label={translate("QMS/INTEGRATION/FIELD_FILENAME")}>
          <input
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full rounded-md border border-grey4 bg-grey2 px-3 py-1.5 text-sm text-white outline-hidden focus:border-yellow1"
          />
        </FormField>

        <FormField label={translate("QMS/INTEGRATION/FIELD_PAYLOAD")}>
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={8}
            className="font-mono w-full resize-none rounded-md border border-grey4 bg-grey2 px-3 py-2 text-sm leading-relaxed text-white outline-hidden focus:border-yellow1"
          />
        </FormField>
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button
          onClick={handleSend}
          disabled={sending || !webhookToken}
          className="inline-flex items-center gap-2 rounded-lg bg-yellow1 px-4 py-2 text-sm font-medium text-grey1 hover:opacity-90 disabled:opacity-40"
        >
          <IconPlugConnected size={14} />
          {sending
            ? translate("QMS/SUPPLIER/SENDING")
            : translate("QMS/SUPPLIER/SEND_WEBHOOK")}
        </button>
        {status && (
          <span
            className={clsx(
              "text-xs",
              status.kind === "ok" ? "text-success" : "text-red1",
            )}
          >
            {status.msg}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Shared form field ──────────────────────────────────────────────────────────

function FormField({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-grey5">
        {label}
      </label>
      {children}
    </div>
  )
}
